import { Children, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogoIcon } from '@/assets/icons';
import { ROUTES } from '@/constants';
import { cn } from '@/libs/utils';
import TruncateTooltipCell from '../data-table/TruncateTooltipCell';
import { Header } from '../layouts';
import { TextSkeleton } from '../skeletons';

// Room the title keeps before the toolbar buttons start to shrink.
const TITLE_MIN_WIDTH = 180;
const ROW_GAP = 16;
// Spelled out in full: Tailwind drops component classes it cannot find verbatim.
const DENSITY_CLASSES = [
  '',
  'content-header__toolbar--dense-1',
  'content-header__toolbar--dense-2',
  'content-header__toolbar--dense-3',
];
const LAST_DENSITY = DENSITY_CLASSES.length - 1;

/**
 * Picks how dense the toolbar is: full-size buttons while they fit beside the
 * title, smaller ones (density 1 to 3) once they no longer do. Natural widths
 * are remembered per density so it can step back up when room comes back.
 */
function useToolbarDensity(headerRef, toolbarRef, childCount) {
  const [density, setDensity] = useState(0);
  const widths = useRef([]);

  useLayoutEffect(() => {
    widths.current = [];
    setDensity(0);
  }, [childCount]);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const toolbar = toolbarRef.current;
    if (!header || !toolbar || typeof ResizeObserver === 'undefined') return undefined;

    // The header is the box that clips; the row inside it grows with its
    // content, so its own width never shows a shortage.
    const measure = () => {
      const css = window.getComputedStyle(header);
      const inner = header.clientWidth - parseFloat(css.paddingLeft) - parseFloat(css.paddingRight);
      const available = inner - TITLE_MIN_WIDTH - ROW_GAP;
      setDensity((current) => {
        widths.current[current] = toolbar.scrollWidth;
        if (widths.current[current] > available && current < LAST_DENSITY) return current + 1;
        if (current > 0 && widths.current[current - 1] != null && widths.current[current - 1] <= available) return current - 1;
        return current;
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [headerRef, toolbarRef, density]);

  return density;
}

export default function ContentHeader({
  className,
  label,
  title,
  isLoading,
  children,
  style,
}) {
  const headerRef = useRef(null);
  const toolbarRef = useRef(null);
  const density = useToolbarDensity(headerRef, toolbarRef, Children.count(children));

  return (
    <Header ref={headerRef} className={cn('content-header', className)} style={style}>
      <div className="flex items-center justify-between gap-4">
        {title ? (
          // 🚨 `min-w-0` on both, or the title never gives way. A flex/grid item
          // defaults to `min-width: auto`, which refuses to shrink below its own
          // content — so on a narrow screen the h3 keeps its full width and, being
          // `whitespace-nowrap`, simply runs underneath the toolbar. The ellipsis
          // machinery in TruncateTooltipCell is already there; it just never got a
          // constrained box to work in.
          <div className="grid min-w-0 gap-1.5">
            {label && (
              <span className="text-[13px] font-semibold text-zinc-200">
                {label}
              </span>
            )}

            {!isLoading ? (
              <h3 className="flex min-w-0 items-center whitespace-nowrap text-xl font-bold md:text-2xl">
                <TruncateTooltipCell label={title} maxLength={36} />
              </h3>
            ) : (
              <TextSkeleton />
            )}
          </div>
        ) : (
          <div className="relative mx-auto grid justify-center">
            {!isLoading && (
              <Link to={ROUTES.admin}>
                <LogoIcon />
              </Link>
            )}
          </div>
        )}

        {/*
          🚨 One row, and it must stay one row. The header is fixed at
          `--headerHeight` with `overflow-y-hidden`, so a toolbar allowed to wrap
          grows past the box it lives in and — being vertically centred — climbs
          out of the top and paints over the table's own header. Measured on the
          invoice book at 1.150px: two lines, 76px of toolbar in a 68px header.
          Crowded screens scroll sideways instead; that is what `overflow-x-auto`
          on `.content-header` is for, and it is how every other table behaves.
          `shrink-0` is what makes that scroll work — without it the buttons
          squeeze themselves into illegible slivers rather than overflow.
        */}
        <div
          ref={toolbarRef}
          className={cn(
            'content-header__toolbar flex items-center justify-end gap-3 [&>*]:shrink-0',
            DENSITY_CLASSES[density]
          )}
        >
          {children}
        </div>
      </div>
    </Header>
  );
}
